const CP1252_UNICODE_TO_BYTE: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

const CP1252_BYTE_TO_UNICODE: Record<number, number> = Object.fromEntries(
  Object.entries(CP1252_UNICODE_TO_BYTE).map(([unicode, byte]) => [byte, Number(unicode)]),
);

const MOJIBAKE_TOKENS = [
  '\u00c3',
  '\u00c2',
  '\u00c4',
  '\u00c5',
  '\u00e2\u20ac\u2122',
  '\u00ef\u00bf\u00bd',
];

const COMMON_REPLACEMENT_FALLBACKS: Array<[string, string]> = [
  ['\u00ef\u00bf\u00bdge', 'âge'],
  ['M\u00ef\u00bf\u00bdle', 'Mâle'],
  ['refei\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'refeição'],
  ['Refei\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'Refeição'],
  ['refei\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdes', 'refeições'],
  ['Refei\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdes', 'Refeições'],
  ['Caf\u00ef\u00bf\u00bd', 'Café'],
  ['manh\u00ef\u00bf\u00bd', 'manhã'],
  ['Hidrata\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'Hidratação'],
  ['hidrata\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'hidratação'],
  ['sugest\u00ef\u00bf\u00bdes', 'sugestões'],
  ['est\u00ef\u00bf\u00bdo', 'estão'],
  ['pr\u00ef\u00bf\u00bdximos', 'próximos'],
  ['n\u00ef\u00bf\u00bdo', 'não'],
  ['poss\u00ef\u00bf\u00bdvel', 'possível'],
  ['c\u00ef\u00bf\u00bdmera', 'câmera'],
  ['C\u00ef\u00bf\u00bdmera', 'Câmera'],
  ['permiss\u00ef\u00bf\u00bdo', 'permissão'],
  ['conex\u00ef\u00bf\u00bdo', 'conexão'],
  ['vis\u00ef\u00bf\u00bdo', 'visão'],
  ['nutri\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'nutrição'],
  ['defini\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'definição'],
  ['por\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'porção'],
  ['dura\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'duração'],
  ['m\u00ef\u00bf\u00bds', 'mês'],
  ['m\u00ef\u00bf\u00bddio', 'médio'],
  ['op\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'opção'],
  ['usu\u00ef\u00bf\u00bdrio', 'usuário'],
  ['di\u00ef\u00bf\u00bdria', 'diária'],
  ['a\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'ação'],
  ['autoriza\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'autorização'],
  ['aplica\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'aplicação'],
  ['renova\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'renovação'],
  ['medita\u00ef\u00bf\u00bd\u00ef\u00bf\u00bdo', 'meditação'],
  ['cafe\u00ef\u00bf\u00bdna', 'cafeína'],
];

const UTF8_ENCODER = new TextEncoder();

const buildMojibakeReplacements = () => {
  const decodeLatin1 = (bytes: Uint8Array) =>
    Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');

  const decodeCp1252 = (bytes: Uint8Array) =>
    Array.from(bytes, (byte) => String.fromCodePoint(CP1252_BYTE_TO_UNICODE[byte] ?? byte)).join('');

  const originals: string[] = [];
  for (let code = 0x00a0; code <= 0x017f; code += 1) {
    originals.push(String.fromCodePoint(code));
  }
  for (const code of [
    0x0192, 0x02c6, 0x02dc, 0x2013, 0x2014, 0x2018, 0x2019, 0x201a, 0x201c, 0x201d, 0x201e,
    0x2020, 0x2021, 0x2022, 0x2026, 0x2030, 0x2039, 0x203a, 0x20ac, 0x2122, 0x2192,
  ]) {
    originals.push(String.fromCodePoint(code));
  }

  const pairs = new Map<string, string>();
  for (const original of originals) {
    const utf8Bytes = UTF8_ENCODER.encode(original);
    for (const mojibake of [decodeLatin1(utf8Bytes), decodeCp1252(utf8Bytes)]) {
      if (mojibake !== original) {
        pairs.set(mojibake, original);
      }
    }
  }

  return Array.from(pairs.entries()).sort((left, right) => right[0].length - left[0].length);
};

const MOJIBAKE_REPLACEMENTS = buildMojibakeReplacements();

const hasLikelyMojibake = (value: string) =>
  MOJIBAKE_TOKENS.some((token) => value.includes(token));

const countIssues = (value: string) =>
  MOJIBAKE_TOKENS.reduce(
    (count, token) => count + (value.split(token).length - 1),
    0,
  );

const encodeWindows1252 = (value: string) =>
  Uint8Array.from(
    Array.from(value).map((char) => {
      const codePoint = char.codePointAt(0) ?? 0x3f;
      if (codePoint <= 0xff) return codePoint;
      return CP1252_UNICODE_TO_BYTE[codePoint] ?? 0x3f;
    }),
  );

const decodeUtf8FromWindows1252 = (value: string) => {
  try {
    return new TextDecoder('utf-8', { fatal: false }).decode(encodeWindows1252(value));
  } catch {
    return value;
  }
};

const replaceKnownMojibake = (value: string) => {
  let current = value;

  for (const [mojibake, original] of MOJIBAKE_REPLACEMENTS) {
    if (current.includes(mojibake)) {
      current = current.split(mojibake).join(original);
    }
  }

  for (const [broken, fixed] of COMMON_REPLACEMENT_FALLBACKS) {
    if (current.includes(broken)) {
      current = current.split(broken).join(fixed);
    }
  }

  return current;
};

export const fixMojibakeString = (value: string) => {
  if (!hasLikelyMojibake(value)) return value;

  let current = replaceKnownMojibake(value);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const next = decodeUtf8FromWindows1252(current);
    if (!next || next === current) break;
    if (countIssues(next) > countIssues(current) && attempt > 0) break;
    current = replaceKnownMojibake(next);
    if (!hasLikelyMojibake(current)) break;
  }

  return replaceKnownMojibake(current);
};

export const normalizeTranslationValue = <T,>(value: T): T => {
  if (typeof value === 'string') return fixMojibakeString(value) as T;
  if (Array.isArray(value)) return value.map((item) => normalizeTranslationValue(item)) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        normalizeTranslationValue(entry),
      ]),
    ) as T;
  }
  return value;
};
